// Maps API records onto per-item checklist completion. Pure mappers,
// exported for tests; the fetching lives in app/actions/academySetup.
// completionFromApi reads the consolidated GET /academy/setup response, which
// serves all nine flags since 2026-09-03; deriveCompletion is the retired
// client-side derivation from four records, kept as the reference/fallback
// should the endpoint's flags need re-deriving locally
// (see _plans/academy-trial-onboarding-spec-v2.md B1).
import { OnboardingModel } from "app/models";
import { FacilityModel } from "app/models/scheduling/FacilityModel";
import { ICoreFacilityV1, ILessonRate } from "app/routes/AcademyWizard/api";
import {
  AcademySetupCompletion,
  AcademySetupItemKey,
  AcademySetupOptionalState,
  AcademySetupOptionalStates,
  academySetupItemConfigs,
} from "./items";

/**
 * The scheduling coach profile fields the checklist reads — the same
 * `coach_profiles/:id` record /billing and /account/coach-profile read.
 */
export interface ICoachProfileStatus {
  stripeAccountId?: string | null;
  publicLandingPage?: boolean;
  /** The coach's own rates — the same list /calendar/pricing edits. */
  lessonRates?: ILessonRate[];
}

export interface AcademySetupSources {
  /** Core v1 `/facilities` first record — the /manage_facility General tab. */
  coreFacility?: ICoreFacilityV1;
  /** Scheduling `facility` — landing page toggle + facility-level Stripe. */
  schedulingFacility?: FacilityModel;
  /** Scheduling `coach_profiles/:id` — coach-level Stripe + profile toggle. */
  coachProfile?: ICoachProfileStatus;
  /** Core v1 `account/onboarding` — space counters. */
  onboarding?: OnboardingModel;
}

// The wizard General step's required fields (AcademyWizard/validation.ts):
// name, street address, city and ZIP everywhere, plus State for US.
const facilityProfileComplete = (facility?: ICoreFacilityV1): boolean => {
  if (!facility) {
    return false;
  }
  const required = [facility.name, facility.street1, facility.city, facility.zip];
  if (facility.country === "US") {
    required.push(facility.state);
  }
  return required.every((value) => Boolean(value && value.trim()));
};

/**
 * Core `GET /academy/setup` response. The BE serializes the checklist keys
 * as snake_case (facility_profile, invite_athlete, …) but the core fetch
 * layer camelizes every response key (successHandler → serialize), so it
 * arrives like this.
 */
export interface IAcademySetupApiResponse {
  facilityProfile?: boolean;
  /** Served as `availability` — the checklist item is calendar_options. */
  availability?: boolean;
  bookingOptions?: boolean;
  stripe?: boolean;
  /** Served as `packages` — the checklist item is packages_subscriptions. */
  packages?: boolean;
  coachProfile?: boolean;
  publicPresence?: boolean;
  inviteAthlete?: boolean;
  /** Served as `mobile_app` — the BE reuses the checklist item id, not the onboarding field name. */
  mobileApp?: boolean;
  /**
   * Per-coach state of the optional items (live on staging 2026-09-04):
   * `visited_items` / `skipped_items` are arrays of the BE's item ids —
   * the same keys the flags above use (stripe, packages, public_presence),
   * see optionalItemApiIds. Arrays, not a keyed object, because the fetch
   * layer camelizes every response KEY — values are left alone, so the ids
   * survive in transit. Written through PUT academy/setup/items/:item_id
   * { status }.
   */
  visitedItems?: string[];
  skippedItems?: string[];
}

/**
 * The BE's id for each optional item. The API keys the checklist the same
 * way everywhere — the GET flags, visited_items/skipped_items and the PUT
 * path — while the checklist keys are the spec §6.5 item_ids the analytics
 * events carry, so the FE translates at the API boundary (agreed with the
 * BE 2026-09-04) exactly as completionFromApi does for the flags.
 */
export const optionalItemApiIds: { [key in AcademySetupItemKey]?: string } = {
  stripe_connect: "stripe",
  packages_subscriptions: "packages",
  academy_page: "public_presence",
};

/** All nine flags are served by the endpoint (2026-09-03); absent = incomplete. */
export const completionFromApi = (
  response: IAcademySetupApiResponse,
): AcademySetupCompletion => {
  return {
    facility_profile: Boolean(response.facilityProfile),
    calendar_options: Boolean(response.availability),
    booking_options: Boolean(response.bookingOptions),
    stripe_connect: Boolean(response.stripe),
    packages_subscriptions: Boolean(response.packages),
    coach_profile: Boolean(response.coachProfile),
    academy_page: Boolean(response.publicPresence),
    invite_athlete: Boolean(response.inviteAthlete),
    mobile_app: Boolean(response.mobileApp),
  };
};

const optionalKeys = academySetupItemConfigs
  .filter((item) => item.optional)
  .map((item) => item.key);

/**
 * The persisted visited/skipped state of the optional items, translated
 * from the BE ids. Skipped wins when the BE lists an item under both (the
 * explicit act is the stronger funnel signal); ids that aren't optional
 * checklist items are dropped.
 */
export const optionalStatesFromApi = (
  response: IAcademySetupApiResponse,
): AcademySetupOptionalStates => {
  const states: AcademySetupOptionalStates = {};
  const assign = (ids: string[] | undefined, state: AcademySetupOptionalState) => {
    (ids || []).forEach((id) => {
      const key = optionalKeys.find((k) => optionalItemApiIds[k] === id);
      if (key) {
        states[key] = state;
      }
    });
  };
  assign(response.visitedItems, "visited");
  assign(response.skippedItems, "skipped");
  return states;
};

export const deriveCompletion = (
  sources: AcademySetupSources,
): AcademySetupCompletion => {
  const { coreFacility, schedulingFacility, coachProfile, onboarding } =
    sources;

  // Facilities with facility-level Stripe connect on /facility_billing and
  // store the account on the facility record; everyone else connects the
  // coach profile on /billing. "Connected" = an account id is present — the
  // same field both Setup tabs surface as stripeId.
  const stripeAccountId = schedulingFacility?.hasFacilityLevelStripe
    ? schedulingFacility?.stripeAccountId
    : coachProfile?.stripeAccountId;

  return {
    facility_profile: facilityProfileComplete(coreFacility),
    stripe_connect: Boolean(stripeAccountId),
    invite_athlete: (onboarding?.created_spaces_count || 0) > 0,
    // Active only — /calendar/pricing itself requires at least one active
    // rate for bookings; a missing flag counts (the wizard defaults it true).
    booking_options: Boolean(
      coachProfile?.lessonRates?.some(
        (rate) => rate.active !== false && !rate._destroy,
      ),
    ),
    coach_profile: Boolean(coachProfile?.publicLandingPage),
    academy_page: Boolean(schedulingFacility?.publicLandingPage),
    // No client-side sources exist for the spec v2 items (availability time
    // tables, stripe products, mobile sessions) — the BE endpoint owns them.
    calendar_options: false,
    packages_subscriptions: false,
    mobile_app: false,
  };
};
